import React, { useEffect, useMemo, useState } from 'react';
import {
  listTenants,
  listServicesForTenant,
  getTenantServiceSettings,
  compareSettings,
} from '../../services/envScanApi';

const PORTAL_BASE = 'https://portal.azure.com/#@/resource/subscriptions';
const SUBSCRIPTION_ID = '847159ba-38f0-47c5-a009-917bb28a443c';

const portalLinkFor = (app) =>
  app
    ? `${PORTAL_BASE}/${SUBSCRIPTION_ID}/resourceGroups/${encodeURIComponent(app.resourceGroup)}/providers/Microsoft.Web/sites/${encodeURIComponent(app.name)}/configuration`
    : null;

const formatServiceType = (s) =>
  (s || '').split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/**
 * Side panel: shows one setting's value across other tenants of the same service type.
 */
const DiffPanel = ({ tenant, service, settingName, currentValue, onClose }) => {
  const [tenants, setTenants] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const allTenants = await listTenants();
        const others = allTenants.filter((t) => t !== tenant).slice(0, 4);
        if (cancelled) return;
        setTenants([tenant, ...others]);
        const data = await compareSettings([tenant, ...others], service, showSecrets);
        if (!cancelled) setResults(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load comparison');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenant, service, showSecrets]);

  const rows = useMemo(() => {
    if (!results) return [];
    const byTenant = {};
    for (const app of results) {
      const setting = app.settings.find((s) => s.name === settingName);
      byTenant[app.tenantName] = setting ? setting.value : null;
    }
    return tenants.map((t) => ({
      tenant: t,
      value: byTenant[t] === undefined ? null : byTenant[t],
      missing: !(t in byTenant) || byTenant[t] === null,
    }));
  }, [results, tenants, settingName]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-white dark:bg-slate-900 h-full overflow-y-auto p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-400">Comparing</p>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 break-all">{settingName}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Service type: <span className="font-mono">{service}</span> · Other tenants: top 4
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none"
            aria-label="Close"
          >×</button>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={showSecrets}
            onChange={(e) => setShowSecrets(e.target.checked)}
            className="w-4 h-4"
          />
          Show secrets
        </label>

        {loading && (
          <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400 text-sm">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
            Loading values from other tenants…
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 rounded-lg text-sm">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-2">
            <div className="p-3 rounded-lg border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950">
              <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase mb-1">
                {tenant} (current)
              </div>
              <pre className="text-xs font-mono break-all whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                {currentValue ?? '(not set)'}
              </pre>
            </div>
            {rows.filter((r) => r.tenant !== tenant).map((r) => {
              const sameAsCurrent = r.value === currentValue;
              return (
                <div
                  key={r.tenant}
                  className={`p-3 rounded-lg border ${
                    r.missing
                      ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950'
                      : sameAsCurrent
                        ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950'
                        : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold uppercase">{r.tenant}</span>
                    {r.missing && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-200 text-red-800">Missing</span>
                    )}
                    {!r.missing && sameAsCurrent && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-200 text-green-800">Same</span>
                    )}
                    {!r.missing && !sameAsCurrent && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-200 text-amber-800">Different</span>
                    )}
                  </div>
                  {!r.missing && (
                    <pre className="text-xs font-mono break-all whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                      {r.value}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const Inspector = () => {
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState(null);

  const [selectedTenant, setSelectedTenant] = useState('');
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [servicesLoading, setServicesLoading] = useState(false);

  const [appData, setAppData] = useState(null);
  const [appLoading, setAppLoading] = useState(false);
  const [appError, setAppError] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);

  const [search, setSearch] = useState('');
  const [diffSetting, setDiffSetting] = useState(null); // { name, value }

  // Load tenants on mount
  useEffect(() => {
    let cancelled = false;
    setTenantsLoading(true);
    listTenants()
      .then((list) => {
        if (cancelled) return;
        setTenants(list || []);
        if (list && list.length > 0) setSelectedTenant(list[0]);
      })
      .catch((err) => !cancelled && setTenantsError(err.message))
      .finally(() => !cancelled && setTenantsLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Load service types when tenant changes
  useEffect(() => {
    if (!selectedTenant) return;
    let cancelled = false;
    setServicesLoading(true);
    setServices([]);
    setSelectedService('');
    setAppData(null);
    listServicesForTenant(selectedTenant)
      .then((list) => {
        if (cancelled) return;
        setServices(list || []);
        if (list && list.length > 0) setSelectedService(list[0]);
      })
      .catch(() => !cancelled && setServices([]))
      .finally(() => !cancelled && setServicesLoading(false));
    return () => { cancelled = true; };
  }, [selectedTenant]);

  // Load App Service settings when tenant + service are picked
  useEffect(() => {
    if (!selectedTenant || !selectedService) return;
    let cancelled = false;
    setAppLoading(true);
    setAppError(null);
    getTenantServiceSettings(selectedTenant, selectedService, showSecrets)
      .then((data) => !cancelled && setAppData(data))
      .catch((err) => !cancelled && setAppError(err.message || 'Failed to load settings'))
      .finally(() => !cancelled && setAppLoading(false));
    return () => { cancelled = true; };
  }, [selectedTenant, selectedService, showSecrets]);

  const filteredSettings = useMemo(() => {
    if (!appData?.settings) return [];
    if (!search.trim()) return appData.settings;
    const q = search.trim().toLowerCase();
    return appData.settings.filter(
      (s) => s.name.toLowerCase().includes(q) || (s.value || '').toLowerCase().includes(q)
    );
  }, [appData, search]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Environment Inspector</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Pick a tenant and service type to see its App Service settings. Click any row to compare against other tenants.
        </p>
      </div>

      {tenantsError && (
        <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg mb-4">
          <strong>Failed to load tenants.</strong> {tenantsError}
          <p className="text-xs mt-1">
            If this dashboard is deployed and you see auth errors, the App Service Managed Identity probably needs Reader + Website Contributor on the prod subscription.
          </p>
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tenant</label>
            <select
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              disabled={tenantsLoading || tenants.length === 0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
            >
              {tenantsLoading && <option>Loading…</option>}
              {!tenantsLoading && tenants.length === 0 && <option>No tenants found</option>}
              {tenants.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service Type</label>
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              disabled={servicesLoading || services.length === 0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
            >
              {servicesLoading && <option>Loading…</option>}
              {!servicesLoading && services.length === 0 && <option>None</option>}
              {services.map((s) => (
                <option key={s} value={s}>{formatServiceType(s)}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={showSecrets}
                onChange={(e) => setShowSecrets(e.target.checked)}
                className="w-4 h-4"
              />
              Show secrets
            </label>
            {appData && (
              <a
                href={portalLinkFor(appData)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline"
              >
                Open in Portal ↗
              </a>
            )}
          </div>
        </div>
      </div>

      {appError && (
        <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg mb-4">
          {appError}
        </div>
      )}

      {appLoading && (
        <div className="flex items-center gap-2 p-6 text-gray-500 dark:text-slate-400">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
          Loading settings for {selectedTenant} / {formatServiceType(selectedService)}…
        </div>
      )}

      {appData && !appLoading && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-mono text-sm text-gray-700 dark:text-gray-300">{appData.name}</p>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {appData.resourceGroup} · {appData.location} · {appData.settings.length} setting{appData.settings.length === 1 ? '' : 's'}
              </p>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search settings…"
              className="w-72 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 text-sm"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-950">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-700 dark:text-gray-300 w-1/3">Setting Name</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700 dark:text-gray-300">Value</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-700 dark:text-gray-300 w-32">Compare</th>
                </tr>
              </thead>
              <tbody>
                {filteredSettings.map((s) => (
                  <tr
                    key={s.name}
                    className="border-t border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800"
                  >
                    <td className="px-4 py-2 font-medium font-mono text-xs break-all">{s.name}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600 dark:text-gray-400 break-all">
                      {s.isMasked ? <span className="opacity-60">{s.value}</span> : s.value}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setDiffSetting({ name: s.name, value: s.value })}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        diff ↗
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredSettings.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center py-6 text-gray-400 dark:text-slate-500">
                      No settings match "{search}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {diffSetting && (
        <DiffPanel
          tenant={selectedTenant}
          service={selectedService}
          settingName={diffSetting.name}
          currentValue={diffSetting.value}
          onClose={() => setDiffSetting(null)}
        />
      )}
    </div>
  );
};

export default Inspector;
